/**
 * Filtros del listado (doc 14/09 §2: búsqueda por nombre o mail, área, líder,
 * país, estado) + el "filtro interno" de videos sin tiempo de visualización.
 */
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { LEARNING_STATUSES } from '@/../../shared/people-learning';
import { ALL, statusLabel, type RowFilters } from './learning-dash-utils';

export function ClapperFilters({ filters, onChange, options, show, onExport, exporting = false }: {
  filters: RowFilters;
  onChange: (next: RowFilters) => void;
  options: { areas: string[]; countries: string[]; leaders: Array<{ key: string; label: string }> };
  show: { area: boolean; leader: boolean };
  onExport?: () => void;
  exporting?: boolean;
}) {
  const { t } = useTranslation();
  const k = 'people_learning.dash.filters';
  const set = (patch: Partial<RowFilters>) => onChange({ ...filters, ...patch });
  const select = (value: string, onValue: (v: string) => void, all: string, items: Array<{ key: string; label: string }>) => (
    <Select value={value} onValueChange={onValue}>
      <SelectTrigger className="h-8 w-[170px] text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{all}</SelectItem>
        {items.map((i) => <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input value={filters.search} onChange={(e) => set({ search: e.target.value })} placeholder={t(`${k}.search`, 'Buscar por nombre o email…')} className="h-8 w-56 text-xs" />
      {show.area && select(filters.area, (v) => set({ area: v }), t(`${k}.all_areas`, 'Todas las áreas'), options.areas.map((a) => ({ key: a, label: a })))}
      {show.leader && select(filters.leader, (v) => set({ leader: v }), t(`${k}.all_leaders`, 'Todos los líderes'), options.leaders)}
      {select(filters.country, (v) => set({ country: v }), t(`${k}.all_countries`, 'Todos los países'), options.countries.map((c) => ({ key: c, label: c })))}
      {select(filters.status, (v) => set({ status: v }), t(`${k}.all_statuses`, 'Todos los estados'), LEARNING_STATUSES.map((key) => ({ key, label: statusLabel(t, key) })))}
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Switch checked={filters.suspiciousOnly} onCheckedChange={(v) => set({ suspiciousOnly: v })} />
        {t(`${k}.suspicious_only`, 'Solo videos sin tiempo de visualización')}
      </label>
      {onExport && (
        <Button size="sm" variant="outline" className="ml-auto h-8" onClick={onExport} disabled={exporting}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> {t(`${k}.export`, 'Exportar CSV')}
        </Button>
      )}
    </div>
  );
}
